/* ============================================================================
 * Tykables Polls — storefront web components
 * ----------------------------------------------------------------------------
 * Defines three custom elements:
 *
 *   <tyk-poll poll-id="..." surface="section|account|post_purchase|modal">
 *     Renders a poll inline. Used by the Tykables Poll section, the customer
 *     account dashboard, the thanks page, and (when pre-stuffed) the modal.
 *
 *   <tyk-poll-modal>
 *     Site-wide auto-discovery modal. On page load (logged-in customers only),
 *     asks the backend whether any active modal-surface poll matches the
 *     current page + customer + the visitor's frequency rules. If yes,
 *     schedules a trigger (time, scroll, exit-intent) and presents the poll
 *     in an overlay.
 *
 *   <tyk-poll-account-list>
 *     Customer-account "Feedback" tab. Fetches the list of active
 *     account-surface polls the customer is eligible for and renders one
 *     <tyk-poll> per result.
 *
 * IDENTITY MODEL — logged-in only.
 *   No anonymous response capture (per project memory polls_logged_in_only).
 *   Every surface gates on window.tykPollConfig.customerId (injected by layout/theme.liquid); no customer ⇒ no-op.
 *   No localStorage anon UUID, no impression for guests.
 *
 * Backend contract: docs/POLLS.md → API contract.
 * Backend lives in tykables-theme/workers/backend.js (NOT this repo).
 *
 * Spinner contract: every submit button includes .loading__spinner and the
 * form includes .product-form__error-message-wrapper so the existing Dawn
 * CSS handles loading state without a special case.
 * ==========================================================================*/

(function () {
  'use strict';

  if (window.customElements && customElements.get('tyk-poll')) return;

  // --------------------------------------------------------------------------
  // Constants & helpers
  // --------------------------------------------------------------------------

  var PROXY_BASE = '/apps/pwa/polls';
  var MODAL_CACHE_KEY = 'tyk_poll_modal_check';
  var MODAL_CACHE_TTL_MS = 15 * 60 * 1000; // 15 min

  function getCustomerContext() {
    // Customer state is injected by layout/theme.liquid as window.tykPollConfig
    // (matches the existing tykXyzConfig pattern used by tyk-quiz, etc.).
    var cfg = window.tykPollConfig || {};
    return {
      id: cfg.customerId || null,
      tags: Array.isArray(cfg.customerTags) ? cfg.customerTags : [],
      hasPurchased: !!cfg.hasPurchased,
      country: cfg.country || (window.Shopify && window.Shopify.country) || null,
    };
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function emit(target, name, detail) {
    target.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  }

  function buildQs(params) {
    var parts = [];
    Object.keys(params).forEach(function (k) {
      if (params[k] === undefined || params[k] === null) return;
      parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(params[k]));
    });
    return parts.join('&');
  }

  function fetchJson(url, opts) {
    var o = Object.assign({ credentials: 'same-origin' }, opts || {});
    if (o.body && typeof o.body !== 'string') {
      o.headers = Object.assign({ 'Content-Type': 'application/json' }, o.headers || {});
      o.body = JSON.stringify(o.body);
    }
    return fetch(url, o).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = new Error('http_' + res.status);
          err.payload = data;
          throw err;
        }
        return data;
      });
    });
  }

  // --------------------------------------------------------------------------
  // Question renderers — return HTML strings
  // --------------------------------------------------------------------------

  function renderQuestion(q, idx) {
    var legend = '<legend class="tyk-poll__prompt">' + escapeHtml(q.prompt || '') +
      (q.required ? ' <span class="tyk-poll__required" aria-hidden="true">*</span>' : '') +
      '</legend>';

    var body = '';
    switch (q.type) {
      case 'single':
        body = (q.options || []).map(function (opt, i) {
          var inputId = 'tyk-poll-' + q.id + '-' + i;
          return (
            '<label class="tyk-poll__option" for="' + inputId + '">' +
              '<input type="radio" id="' + inputId + '" name="' + q.id + '"' +
              ' value="' + escapeHtml(opt.value) + '"' + (q.required ? ' required' : '') + '>' +
              '<span class="tyk-poll__option-label">' + escapeHtml(opt.label) + '</span>' +
            '</label>'
          );
        }).join('');
        break;

      case 'multi':
        body = (q.options || []).map(function (opt, i) {
          var inputId = 'tyk-poll-' + q.id + '-' + i;
          return (
            '<label class="tyk-poll__option" for="' + inputId + '">' +
              '<input type="checkbox" id="' + inputId + '" name="' + q.id + '"' +
              ' value="' + escapeHtml(opt.value) + '">' +
              '<span class="tyk-poll__option-label">' + escapeHtml(opt.label) + '</span>' +
            '</label>'
          );
        }).join('');
        break;

      case 'scale':
        var s = q.scale || { min: 0, max: 10, kind: 'numeric' };
        var ticks = [];
        for (var v = s.min; v <= s.max; v++) {
          var inputId = 'tyk-poll-' + q.id + '-' + v;
          ticks.push(
            '<label class="tyk-poll__scale-tick" for="' + inputId + '">' +
              '<input type="radio" id="' + inputId + '" name="' + q.id + '"' +
              ' value="' + v + '"' + (q.required ? ' required' : '') + '>' +
              '<span class="tyk-poll__scale-num">' + v + '</span>' +
            '</label>'
          );
        }
        body =
          '<div class="tyk-poll__scale" data-kind="' + escapeHtml(s.kind || 'numeric') + '">' +
            ticks.join('') +
          '</div>' +
          (s.min_label || s.max_label
            ? '<div class="tyk-poll__scale-labels">' +
                '<span>' + escapeHtml(s.min_label || '') + '</span>' +
                '<span>' + escapeHtml(s.max_label || '') + '</span>' +
              '</div>'
            : '');
        break;

      case 'text':
        var t = q.text || {};
        var max = t.max_length || 280;
        var ph = escapeHtml(t.placeholder || '');
        body = t.multiline
          ? '<textarea class="tyk-poll__text" name="' + q.id + '" maxlength="' + max + '" rows="4"' +
              ' placeholder="' + ph + '"' + (q.required ? ' required' : '') + '></textarea>'
          : '<input type="text" class="tyk-poll__text" name="' + q.id + '" maxlength="' + max + '"' +
              ' placeholder="' + ph + '"' + (q.required ? ' required' : '') + '>';
        break;

      default:
        body = '<p class="tyk-poll__unsupported">This question type isn’t supported by your current theme version.</p>';
    }

    return (
      '<fieldset class="tyk-poll__question" data-q-id="' + escapeHtml(q.id) +
      '" data-q-type="' + escapeHtml(q.type) + '" data-q-index="' + idx + '">' +
        legend +
        body +
        '<div class="tyk-poll__field-error" data-error-for="' + escapeHtml(q.id) + '" hidden></div>' +
      '</fieldset>'
    );
  }

  function collectAnswers(form) {
    var fieldsets = form.querySelectorAll('.tyk-poll__question');
    var answers = {};
    fieldsets.forEach(function (fs) {
      var qid = fs.getAttribute('data-q-id');
      var type = fs.getAttribute('data-q-type');
      switch (type) {
        case 'single': {
          var checked = fs.querySelector('input[type=radio]:checked');
          if (checked) answers[qid] = [checked.value];
          break;
        }
        case 'multi': {
          var boxes = fs.querySelectorAll('input[type=checkbox]:checked');
          answers[qid] = Array.from(boxes).map(function (b) { return b.value; });
          break;
        }
        case 'scale': {
          var pick = fs.querySelector('input[type=radio]:checked');
          if (pick) answers[qid] = Number(pick.value);
          break;
        }
        case 'text': {
          var input = fs.querySelector('input[name="' + qid + '"], textarea[name="' + qid + '"]');
          if (input && input.value.trim() !== '') answers[qid] = input.value.trim();
          break;
        }
      }
    });
    return answers;
  }

  function clientValidate(poll, answers, form) {
    var errors = {};
    (poll.questions || []).forEach(function (q) {
      if (!q.required) return;
      var a = answers[q.id];
      var empty =
        a === undefined ||
        a === null ||
        (Array.isArray(a) && a.length === 0) ||
        (typeof a === 'string' && a.trim() === '');
      if (empty) errors[q.id] = 'This question is required.';
    });
    form.querySelectorAll('.tyk-poll__field-error').forEach(function (el) {
      var qid = el.getAttribute('data-error-for');
      if (errors[qid]) {
        el.textContent = errors[qid];
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    });
    return errors;
  }

  // --------------------------------------------------------------------------
  // <tyk-poll> — inline renderer
  // --------------------------------------------------------------------------

  // FIX-20260908-02: native DOM constructors (HTMLElement) can't be invoked
  // via .call()/.apply() as plain functions -- the old ES5 "pseudo-classical"
  // pattern (function Foo(){ HTMLElement.call(this) }; Foo.prototype =
  // Object.create(HTMLElement.prototype)) throws "Failed to construct
  // 'HTMLElement': Please use the 'new' operator..." per the Custom Elements
  // v1 spec, which only allows real ES6 class-extends syntax. All the
  // .prototype.method = function(){} assignments below still work fine on a
  // real class's prototype -- only the constructor itself needed to change.
  class TykPoll extends HTMLElement {
    constructor() {
      super();
    }
  }

  TykPoll.prototype.connectedCallback = function () {
    if (this._mounted) return;
    this._mounted = true;
    this._customer = getCustomerContext();
    this._pollId = this.getAttribute('poll-id');
    this._surface = this.getAttribute('surface') || 'section';

    // Logged-in only — no customer means no poll. Remove from DOM cleanly.
    if (!this._customer.id) {
      if (this.parentNode) this.parentNode.removeChild(this);
      return;
    }
    if (!this._pollId) {
      this.replaceChildren();
      return;
    }
    this._fetchAndRender();
  };

  TykPoll.prototype._fetchAndRender = function () {
    var self = this;
    var qs = buildQs({
      surface: this._surface,
      customer_id: this._customer.id,
      page: window.location.pathname,
    });
    fetchJson(PROXY_BASE + '/' + encodeURIComponent(this._pollId) + '?' + qs)
      .then(function (data) {
        if (!data.eligible) {
          self.parentNode && self.parentNode.removeChild(self);
          return;
        }
        self._poll = data.poll;
        self._impressionId = data.impression_id;
        self._render();
        emit(self, 'tyk:poll:shown', {
          pollId: self._pollId,
          surface: self._surface,
          impressionId: self._impressionId,
        });
      })
      .catch(function (err) {
        if (window.console) console.warn('[tyk-poll] fetch failed', err);
        self.parentNode && self.parentNode.removeChild(self);
      });
  };

  TykPoll.prototype._render = function () {
    var poll = this._poll;
    var heading = this.getAttribute('heading-override') || poll.title || '';
    var intro = poll.intro || '';

    var html =
      '<form class="tyk-poll__form" novalidate>' +
        (heading ? '<h2 class="tyk-poll__heading">' + escapeHtml(heading) + '</h2>' : '') +
        (intro ? '<p class="tyk-poll__intro">' + escapeHtml(intro) + '</p>' : '') +
        '<div class="tyk-poll__questions">' +
          (poll.questions || []).map(renderQuestion).join('') +
        '</div>' +
        '<div class="product-form__error-message-wrapper" hidden>' +
          '<svg aria-hidden="true" focusable="false" class="icon icon-error" viewBox="0 0 13 13">' +
            '<circle cx="6.5" cy="6.5" r="5.5" fill="none" stroke="currentColor"/>' +
          '</svg>' +
          '<span class="product-form__error-message"></span>' +
        '</div>' +
        '<div class="tyk-poll__actions">' +
          '<button type="submit" class="tyk-poll__submit button button--primary">' +
            '<span class="tyk-poll__submit-label">Submit</span>' +
            '<div class="loading__spinner hidden">' +
              '<svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66">' +
                '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"/>' +
              '</svg>' +
            '</div>' +
          '</button>' +
        '</div>' +
      '</form>' +
      '<div class="tyk-poll__thanks" hidden>' +
        '<p role="status" aria-live="polite" tabindex="-1" class="tyk-poll__thanks-msg">' +
          escapeHtml(poll.thanks_text || 'Thanks for your feedback!') +
        '</p>' +
      '</div>';

    this.innerHTML = html;
    this._wireForm();
  };

  TykPoll.prototype._wireForm = function () {
    var self = this;
    var form = this.querySelector('.tyk-poll__form');
    if (!form) return;
    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      self._submit();
    });
  };

  TykPoll.prototype._submit = function () {
    var self = this;
    var form = this.querySelector('.tyk-poll__form');
    var btn = this.querySelector('.tyk-poll__submit');
    var spinner = btn && btn.querySelector('.loading__spinner');
    var errBox = this.querySelector('.product-form__error-message-wrapper');
    var errMsg = errBox && errBox.querySelector('.product-form__error-message');

    var answers = collectAnswers(form);
    var errors = clientValidate(this._poll, answers, form);
    if (Object.keys(errors).length > 0) {
      var firstBad = form.querySelector('.tyk-poll__field-error:not([hidden])');
      if (firstBad) firstBad.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }

    btn.disabled = true;
    if (spinner) spinner.classList.remove('hidden');
    if (errBox) errBox.hidden = true;

    fetchJson(PROXY_BASE + '/' + encodeURIComponent(this._pollId) + '/respond', {
      method: 'POST',
      body: {
        answers: answers,
        customer_id: this._customer.id,
        surface: this._surface,
        impression_id: this._impressionId,
        page_path: window.location.pathname,
        referrer: document.referrer || null,
      },
    })
      .then(function (data) {
        if (!data.ok) {
          if (data.errors) {
            form.querySelectorAll('.tyk-poll__field-error').forEach(function (el) {
              var qid = el.getAttribute('data-error-for');
              if (data.errors[qid]) {
                el.textContent = data.errors[qid];
                el.hidden = false;
              }
            });
          }
          throw new Error('validation');
        }
        self._showThanks(data.thanks_text);
        emit(self, 'tyk:poll:submitted', {
          pollId: self._pollId,
          surface: self._surface,
          answers: answers,
        });
      })
      .catch(function (err) {
        if (window.console) console.warn('[tyk-poll] submit failed', err);
        if (errMsg) errMsg.textContent = 'Sorry, we couldn’t save that. Please try again.';
        if (errBox) errBox.hidden = false;
        emit(self, 'tyk:poll:error', { pollId: self._pollId, error: String(err) });
      })
      .finally(function () {
        btn.disabled = false;
        if (spinner) spinner.classList.add('hidden');
      });
  };

  TykPoll.prototype._showThanks = function (text) {
    var form = this.querySelector('.tyk-poll__form');
    var thanks = this.querySelector('.tyk-poll__thanks');
    if (!form || !thanks) return;
    if (text) {
      var msg = thanks.querySelector('.tyk-poll__thanks-msg');
      if (msg) msg.textContent = text;
    }
    form.hidden = true;
    thanks.hidden = false;
    var msg2 = thanks.querySelector('.tyk-poll__thanks-msg');
    if (msg2 && msg2.focus) {
      try { msg2.focus({ preventScroll: false }); } catch (e) { msg2.focus(); }
    }
  };

  customElements.define('tyk-poll', TykPoll);

  // --------------------------------------------------------------------------
  // <tyk-poll-modal> — site-wide auto-discovery + trigger logic
  // Logged-in only. Guests skip the discover call entirely.
  // --------------------------------------------------------------------------

  // FIX-20260908-02: see note above TykPoll -- same ES5-extends-HTMLElement
  // fix, same reason.
  class TykPollModal extends HTMLElement {
    constructor() {
      super();
    }
  }

  TykPollModal.prototype.connectedCallback = function () {
    if (this._mounted) return;
    this._mounted = true;
    if (document.body.dataset.tykPollsDisabled === 'true') return;
    this._customer = getCustomerContext();
    if (!this._customer.id) return; // guest visitors don't trigger polls
    this._discover();
  };

  TykPollModal.prototype._discover = function () {
    var cached = this._readCache();
    if (cached && cached.next_check_at && cached.next_check_at > Date.now()) {
      return;
    }
    var qs = buildQs({
      surface: 'modal',
      customer_id: this._customer.id,
      page: window.location.pathname,
    });
    var self = this;
    fetchJson(PROXY_BASE + '/discover?' + qs)
      .then(function (data) {
        if (!data || !data.poll) {
          self._writeCache({ next_check_at: Date.now() + MODAL_CACHE_TTL_MS });
          return;
        }
        self._poll = data.poll;
        self._impressionId = data.impression_id;
        self._scheduleTrigger();
      })
      .catch(function (err) {
        if (window.console) console.debug('[tyk-poll-modal] discover failed', err);
        // Cache the failure too — without this, every page load fires a
        // fresh /discover when the Worker is unreachable. 15-minute TTL
        // applies to failures the same as to "nothing eligible" responses.
        self._writeCache({ next_check_at: Date.now() + MODAL_CACHE_TTL_MS });
      });
  };

  TykPollModal.prototype._readCache = function () {
    try { return JSON.parse(localStorage.getItem(MODAL_CACHE_KEY) || 'null'); }
    catch (e) { return null; }
  };

  TykPollModal.prototype._writeCache = function (obj) {
    try { localStorage.setItem(MODAL_CACHE_KEY, JSON.stringify(obj)); }
    catch (e) { /* swallow */ }
  };

  TykPollModal.prototype._scheduleTrigger = function () {
    var s = (this._poll.surfaces && this._poll.surfaces.modal) || {};
    var trigger = s.trigger || 'time';
    var self = this;

    if (trigger === 'manual') {
      window.tykPoll = window.tykPoll || {};
      window.tykPoll.openModal = function () { self._open(); };
      return;
    }

    if (trigger === 'time') {
      this._timer = setTimeout(function () { self._open(); }, s.delay_ms || 8000);
      return;
    }

    if (trigger === 'scroll') {
      var pct = (s.scroll_pct || 50) / 100;
      var onScroll = function () {
        var doc = document.documentElement;
        var max = doc.scrollHeight - window.innerHeight;
        if (max <= 0) return;
        if (window.scrollY / max >= pct) {
          window.removeEventListener('scroll', onScroll);
          self._open();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      this._cleanup = function () { window.removeEventListener('scroll', onScroll); };
      return;
    }

    if (trigger === 'exit') {
      var onLeave = function (ev) {
        if (ev.clientY <= 4) {
          document.removeEventListener('mouseleave', onLeave);
          self._open();
        }
      };
      document.addEventListener('mouseleave', onLeave);
      this._cleanup = function () { document.removeEventListener('mouseleave', onLeave); };
      return;
    }
  };

  TykPollModal.prototype._open = function () {
    if (this._opened) return;
    this._opened = true;
    if (this._cleanup) this._cleanup();

    var overlay = document.createElement('div');
    overlay.className = 'tyk-poll-modal__overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', this._poll.title || 'Quick survey');

    var card = document.createElement('div');
    card.className = 'tyk-poll-modal__card';
    overlay.appendChild(card);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'tyk-poll-modal__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML =
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 18 18">' +
        '<path d="M4 4l10 10M14 4L4 14" stroke="currentColor" stroke-width="1.5" fill="none"/>' +
      '</svg>';
    card.appendChild(closeBtn);

    var inner = document.createElement('tyk-poll');
    inner.setAttribute('poll-id', this._poll.id);
    inner.setAttribute('surface', 'modal');
    inner._poll = this._poll;
    inner._impressionId = this._impressionId;
    inner._customer = this._customer;
    inner._pollId = this._poll.id;
    inner._surface = 'modal';
    inner._mounted = true;
    card.appendChild(inner);
    inner._render();

    document.body.appendChild(overlay);
    document.body.classList.add('tyk-poll-modal-open');
    document.body.style.overflow = 'hidden';

    var self = this;
    function close(reason) {
      if (!overlay.parentNode) return;
      overlay.parentNode.removeChild(overlay);
      document.body.classList.remove('tyk-poll-modal-open');
      document.body.style.overflow = '';
      if (reason === 'dismiss') {
        fetchJson(PROXY_BASE + '/' + encodeURIComponent(self._poll.id) + '/dismiss', {
          method: 'POST',
          body: {
            customer_id: self._customer.id,
            surface: 'modal',
            impression_id: self._impressionId,
          },
        }).catch(function () { /* non-critical */ });
        emit(self, 'tyk:poll:dismissed', { pollId: self._poll.id, surface: 'modal' });
      }
    }

    closeBtn.addEventListener('click', function () { close('dismiss'); });
    overlay.addEventListener('click', function (ev) {
      if (ev.target === overlay) close('dismiss');
    });
    document.addEventListener('keydown', function onKey(ev) {
      if (ev.key === 'Escape') {
        close('dismiss');
        document.removeEventListener('keydown', onKey);
      }
    });
    inner.addEventListener('tyk:poll:submitted', function () {
      setTimeout(function () { close('completed'); }, 1800);
    });

    setTimeout(function () {
      var firstInput = card.querySelector('input, textarea, button');
      if (firstInput) firstInput.focus();
    }, 0);

    emit(this, 'tyk:poll:shown', {
      pollId: this._poll.id,
      surface: 'modal',
      impressionId: this._impressionId,
    });
  };

  customElements.define('tyk-poll-modal', TykPollModal);

  // --------------------------------------------------------------------------
  // <tyk-poll-account-list> — customer account "Feedback" tab
  // Already gated on customer.id by definition.
  // --------------------------------------------------------------------------

  // FIX-20260908-02: see note above TykPoll -- same ES5-extends-HTMLElement
  // fix, same reason.
  class TykPollAccountList extends HTMLElement {
    constructor() {
      super();
    }
  }

  TykPollAccountList.prototype.connectedCallback = function () {
    if (this._mounted) return;
    this._mounted = true;
    var customer = getCustomerContext();
    if (!customer.id) return;
    var qs = buildQs({
      customer_id: customer.id,
      page: window.location.pathname,
    });
    var self = this;
    fetchJson(PROXY_BASE + '/account-list?' + qs)
      .then(function (data) {
        if (!data || !Array.isArray(data.polls) || data.polls.length === 0) return;
        var fallback = self.querySelector('[data-tyk-fallback]');
        if (fallback) fallback.remove();
        data.polls.forEach(function (p) {
          var wrap = document.createElement('div');
          wrap.className = 'tyk-poll-account-item tyk-account-panel';
          var el = document.createElement('tyk-poll');
          el.setAttribute('poll-id', p.id);
          el.setAttribute('surface', 'account');
          el.setAttribute('compact', '');
          el._poll = p;
          el._impressionId = p.impression_id;
          el._customer = customer;
          el._pollId = p.id;
          el._surface = 'account';
          el._mounted = true;
          wrap.appendChild(el);
          self.appendChild(wrap);
          el._render();
        });
      })
      .catch(function (err) {
        if (window.console) console.debug('[tyk-poll-account-list] fetch failed', err);
      });
  };

  customElements.define('tyk-poll-account-list', TykPollAccountList);
})();
